import { useState, useMemo } from "react";
import {
  Card,
  BlockStack,
  Text,
  EmptyState,
  Button,
  Box,
  InlineStack,
  InlineGrid,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {
  PlusIcon,
  PlusCircleIcon,
  XIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import AssignLocationsModal from "@features/events/pages/events/components/locations/AssignLocationsModal";
import { Event, Venue } from "@features/events/types";
import { useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useParams } from "react-router-dom";
import EventsDefaultAvatar from "@features/events/components/EventsDefaultAvatar";
import { getLocalizedText } from "@shared/lib";
import emptyLocationImage from "@shared/assets/empty-states/empty-location.svg";

interface LocationsCardProps {
  eventData: Partial<Event>;
  onDataChange: (updates: Partial<Event>) => void;
  fieldErrors?: Record<string, string>;
}

interface LocationItemProps {
  location: Venue;
  onRemove: (locationId: string) => void;
}

const LocationItem: React.FC<LocationItemProps> = ({
  location,
  onRemove,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <SortableResourceItem
      accessibilityLabel={`View details for ${getLocalizedText(location.name) || "location"}`}
      dragHandleAccessibilityLabel={`Reorder ${getLocalizedText(location.name) || "location"}`}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlineGrid columns={["twoThirds", "oneThird"]}>
          <InlineStack gap="200" blockAlign="center" wrap={false}>
            <EventsDefaultAvatar
              name={location.name}
              singleImage={location.videoImage}
              media={location.media}
            />
            <BlockStack gap="100">
              <InlineStack gap="100" align="start" blockAlign="center" wrap={false}>
                <Text as="span" variant="bodyMd" fontWeight="medium">
                  {getLocalizedText(location.name)}
                </Text>
              </InlineStack>
              <Box maxWidth="100%">
                <Text as="span" variant="bodySm" tone="subdued" truncate>
                  {getLocalizedText(location.geoAddress?.summary)}
                </Text>
              </Box>
            </BlockStack>
          </InlineStack>
          <InlineStack
            gap="300"
            align="space-between"
            blockAlign="center"
            wrap={false}
          >
            <Text as="p" variant="bodyMd" tone="subdued">
              {getLocalizedText(location.geoAddress?.summary)}
            </Text>
            <Box opacity={isHovered ? "1" : "0"} paddingInlineEnd="100">
              <InlineStack gap="200" blockAlign="center" align="end">
                <Button
                  icon={XIcon}
                  tone="critical"
                  variant="tertiary"
                  onClick={() => onRemove(location.id)}
                />
              </InlineStack>
            </Box>
          </InlineStack>
        </InlineGrid>
      </div>
    </SortableResourceItem>
  );
};

export default function LocationsCard({
  eventData,
  onDataChange,
  fieldErrors = {},
}: LocationsCardProps) {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const { GET } = useApiRequest();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch all venues to get venue details for assigned venueIds
  const { data: allVenues = [] } = useQuery<Venue[]>({
    queryKey: ["venues", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/venues`),
    enabled: Boolean(hcc),
  });

  // Calculate assigned locations based on venueIds and allVenues using useMemo to prevent infinite loops
  const assignedLocations = useMemo(() => {
    const venueIds = eventData.venueIds || [];

    if (venueIds.length > 0 && allVenues.length > 0) {
      return venueIds
        .map((venueId) => allVenues.find((venue) => venue.id === venueId))
        .filter((venue): venue is Venue => venue !== undefined);
    }
    return [];
  }, [eventData.venueIds, allVenues]);

  const handleAddLocation = () => {
    setIsModalOpen(true);
  };

  const handleAssignLocations = (venueIds: string[]) => {
    // In real app, fetch location details from API
    onDataChange({ venueIds });
    setIsModalOpen(false);
  };

  const handleRemoveLocation = (locationId: string) => {
    const updatedVenueIds = (eventData.venueIds || []).filter(
      (id: string) => id !== locationId
    );
    onDataChange({ venueIds: updatedVenueIds });
  };

  const renderLocationItem = (location: Venue) => (
    <LocationItem
      location={location}
      onRemove={handleRemoveLocation}
    />
  );

  const hasLocations = assignedLocations.length > 0;

  return (
    <>
      <Card
        background={fieldErrors.venueIds ? "bg-surface-critical" : undefined}
      >
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd" requiredIndicator>
                {t("eventDetails.locations.title")}
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                {t("eventDetails.locations.description")}
              </Text>
            </BlockStack>
          </InlineStack>

          {hasLocations ? (
            <BlockStack gap="200">
              <Box
                borderColor="border"
                borderWidth="025"
                borderRadius="200"
                background="bg-surface"
              >
                <SortableList
                  items={assignedLocations}
                  onChange={(reorderedLocations) => {
                    onDataChange({
                      venueIds: reorderedLocations.map((loc) => loc.id),
                    });
                  }}
                  idForItem={(location) => location?.id}
                  renderItem={(item) => renderLocationItem(item)}
                  showDividers={true}
                />
              </Box>
              {hasLocations && (
                <InlineStack>
                  <Button
                    variant="plain"
                    icon={PlusCircleIcon}
                    onClick={handleAddLocation}
                  >
                    {t("eventDetails.locations.addButton")}
                  </Button>
                </InlineStack>
              )}
            </BlockStack>
          ) : (
            <EmptyState
              heading={t("eventDetails.locations.emptyState")}
              action={{
                content: t("eventDetails.locations.addButton"),
                icon: PlusIcon,
                onAction: handleAddLocation,
              }}
              image={emptyLocationImage}
            />
          )}

          {fieldErrors.venueIds && (
            <Text as="p" variant="bodyMd" tone="critical">
              {fieldErrors.venueIds}
            </Text>
          )}
        </BlockStack>
      </Card>

      <AssignLocationsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedLocationIds={eventData.venueIds || []}
        onAssign={handleAssignLocations}
      />
    </>
  );
}
